import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import ProfileMenu from "@/components/ProfileMenu";
import { Briefcase, Users, LayoutGrid, Compass, MessageSquare, CircleUser } from "lucide-react";

// Clean, professional bottom nav for small screens (< lg): flat bar, one line-icon + label per tab.
// Same destinations as before, just a simpler, grown-up look (no raised bubble, no mixed PNG glyphs).
function tabsFor(user) {
  if (!user) return null;
  const core = { to: "/sales-types", label: "C.O.R.E.", Icon: Compass, testid: "tab-core" };
  const messages = { to: "/messages", label: "Messages", Icon: MessageSquare, testid: "tab-messages", badge: true };
  const profile = { kind: "profile", label: "Profile", Icon: CircleUser, testid: "tab-profile" };

  if (user.role === "salesman") {
    return [{ to: "/jobs", label: "Jobs", Icon: Briefcase, testid: "tab-jobs" }, core, messages, profile];
  }
  if (user.role === "business") {
    return [{ to: "/talent", label: "Talent", Icon: Users, testid: "tab-talent" }, core, messages, profile];
  }
  const label = user.role === "brand_ambassador" ? "Dashboard" : user.role === "owner" ? "Control" : "Admin";
  return [{ to: "/admin", label, Icon: LayoutGrid, testid: "tab-admin" }, core, messages, profile];
}

export default function BottomTabBar() {
  const { user } = useAuth();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const tabs = tabsFor(user);

  const loadUnread = () => {
    api.get("/threads/unread-count").then((r) => setUnread(r.data?.count || 0)).catch(() => {});
  };

  useEffect(() => {
    if (!user) return undefined;
    loadUnread();
    const id = setInterval(loadUnread, 30000);
    const onMsg = () => loadUnread();
    window.addEventListener("messages-updated", onMsg);
    return () => { clearInterval(id); window.removeEventListener("messages-updated", onMsg); };
  }, [user, location.pathname]);

  if (!tabs) return null;

  const tabCls = (isActive) =>
    `relative flex-1 flex flex-col items-center justify-center gap-1 py-2 min-w-0 outline-none transition-colors duration-200 ${isActive ? "text-violet-600" : "text-slate-400 hover:text-slate-600 active:text-slate-600"}`;

  const renderContent = (t, isActive) => (
    <>
      <span className="relative">
        <t.Icon className="w-[22px] h-[22px]" strokeWidth={isActive ? 2.4 : 2} />
        {t.badge && unread > 0 && (
          <span data-testid="tab-messages-badge"
            className="absolute -top-1.5 -right-2 min-w-[17px] h-[17px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center justify-center ring-2 ring-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </span>
      <span className="text-[11px] font-semibold tracking-tight leading-none">{t.label}</span>
    </>
  );

  return (
    <nav
      data-testid="bottom-tab-bar"
      className="md:hidden fixed left-3 right-3 z-50 rounded-3xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.12)]"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
    >
      <div className="flex items-stretch px-1.5 py-1.5">
        {tabs.map((t) => {
          if (t.kind === "profile") {
            const trigger = (
              <button data-testid={t.testid} aria-label="Profile" className={tabCls(false)}>
                {renderContent(t, false)}
              </button>
            );
            return <ProfileMenu key={t.testid} side="top" align="end" trigger={trigger} />;
          }
          return (
            <NavLink key={t.testid} to={t.to} data-testid={t.testid} className={({ isActive }) => tabCls(isActive)}>
              {({ isActive }) => renderContent(t, isActive)}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
